import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Modal,
  Pressable,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Circle } from 'react-native-svg';
import { useQueryClient } from '@tanstack/react-query';
import { goalsApi, kpisApi } from '../../services/apiService';
import { exportGoalsToExcel } from '../../utils/excelExport';
import { StrategicGoalList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, GoalsSkeleton, SKELETON_DIMENSIONS, ViewOnlyBadge, Breadcrumb } from '../../components';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, ExportIcon, EllipsisVerticalIcon, EditIcon, TrashIcon } from '../../components/Icons';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useGoalsQuery, useDeleteGoalMutation } from '../../hooks/queries';
import { useYear } from '../../contexts/YearContext';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';

type RouteProps = RouteProp<MainStackParamList, 'Goals'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// Circular Progress Component (matches Figma exactly - 80px)
// ============================================================

interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  textColor?: string;
}

const CircularProgress = ({ percentage, size = 80, strokeWidth = 6, trackColor = '#E5E7EB', textColor = '#414042' }: CircularProgressProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Color based on achievement percentage
  // >= 100%: Green (ممتاز), >= 80%: Orange (جيد), < 80%: Red (يحتاج تحسين)
  let progressColor = '#EF4444'; // Red - Needs Improvement (< 80%)
  if (percentage >= 100) {
    progressColor = '#22C55E'; // Green - Excellent
  } else if (percentage >= 80) {
    progressColor = '#F97316'; // Orange - Good
  }

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle - gray */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      {/* Percentage text in center */}
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={[styles.progressPercentage, { color: textColor }]}>%{percentage}</Text>
      </View>
    </View>
  );
};

// ============================================================
// Goal Card Component (matches Figma node 2432:365762 exactly)
// ============================================================

interface ThemedCardStyles {
  card: { backgroundColor: string; borderColor: string };
  cardTitle: { color: string };
  cardValue: { color: string };
  cardDescription: { color: string };
  sectionTitle: { color: string };
  pageTitle: { color: string };
  pageSubtitle: { color: string };
  statBox: { backgroundColor: string };
  statLabel: { color: string };
  progressText: { color: string };
  detailsText: { color: string };
  iconColor: string;
  dropdownMenu: { backgroundColor: string; borderColor: string };
  dropdownText: { color: string };
  circleTrack: string;
}

interface GoalCardProps {
  goal: StrategicGoalList;
  onPress: () => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  isOrgEmployee?: boolean;
  isViewOnly?: boolean;
  themedStyles: ThemedCardStyles;
}

const GoalCard = ({ goal, onPress, onEdit, onDelete, isOrgEmployee, isViewOnly, themedStyles }: GoalCardProps) => {
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const [menuVisible, setMenuVisible] = useState(false);
  const percentage = goal.progress_percentage ?? 0;
  const weight = parseFloat(goal.weight) || 0;

  const handleEdit = () => {
    setMenuVisible(false);
    onEdit?.(goal.id);
  };

  const handleDelete = () => {
    setMenuVisible(false);
    onDelete?.(goal.id);
  };

  return (
    <View style={[styles.goalCard, themedStyles.card, menuVisible && styles.goalCardMenuOpen]}>
      {/* Top row: Progress circle (left) + Content (right) - RTL layout */}
      <View style={styles.goalCardTopRow}>
        {/* Progress circle on the left */}
        <CircularProgress percentage={percentage} size={80} strokeWidth={6} trackColor={themedStyles.circleTrack} textColor={themedStyles.progressText.color} />

        {/* Content on the right */}
        <View style={styles.goalCardContent}>
          {/* Title */}
          <Text style={[styles.goalCardTitle, themedStyles.cardTitle]} numberOfLines={2}>{translate(goal.title)}</Text>

          {/* Description */}
          {goal.description && (
            <Text style={[styles.goalCardDescription, themedStyles.cardDescription]} numberOfLines={2}>
              {translate(goal.description)}
            </Text>
          )}

          {/* Pending approval badge */}
          {goal.status === 'pending' && (
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>{t('goals.pendingApproval')}</Text>
            </View>
          )}
          {/* Pending deletion badge */}
          {goal.has_pending_deletion && <PendingDeletionBadge compact />}
        </View>
      </View>

      {/* Stats row - individual rounded boxes (RTL order: right to left) */}
      <View style={styles.goalCardStatsRow}>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.relativeWeight')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{Math.round(weight)} %</Text>
        </View>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.kpisCount')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{goal.kpis_count ?? 0}</Text>
        </View>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.processesCount')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{goal.processes_count ?? 0}</Text>
        </View>
        <View style={[styles.goalCardStatBox, themedStyles.statBox]}>
          <Text style={[styles.goalCardStatLabel, themedStyles.statLabel]}>{t('goals.initiativesCount')}</Text>
          <Text style={[styles.goalCardStatValue, themedStyles.cardValue]}>{goal.initiatives_count ?? 0}</Text>
        </View>
      </View>

      {/* Bottom row: Details link (left) + Menu icon (right) */}
      <View style={styles.goalCardBottomRow}>
        {/* Details link on the left */}
        <TouchableOpacity style={styles.goalCardDetailsLink} onPress={onPress}>
          <ChevronLeftIcon size={16} color={themedStyles.iconColor} />
          <Text style={[styles.goalCardDetailsText, themedStyles.detailsText]}>{t('common.viewDetails')}</Text>
        </TouchableOpacity>

        {/* Menu icon on the right with dropdown - hidden for view-only users (executive) */}
        {!isViewOnly && (
          <View style={styles.menuWrapper}>
            <TouchableOpacity
              style={styles.goalCardMenu}
              onPress={() => setMenuVisible(!menuVisible)}
            >
              <EllipsisVerticalIcon size={20} color={themedStyles.iconColor} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Dropdown Menu - positioned outside bottom row for proper layering */}
      {!isViewOnly && menuVisible && (
        <>
          <Pressable
            style={styles.menuOverlay}
            onPress={() => setMenuVisible(false)}
          />
          <View style={[styles.dropdownMenu, themedStyles.dropdownMenu]}>
            <TouchableOpacity style={styles.dropdownMenuItem} onPress={handleEdit}>
              <Text style={[styles.dropdownMenuItemText, themedStyles.dropdownText]}>{t('common.edit')}</Text>
              <EditIcon size={16} color={themedStyles.iconColor} />
            </TouchableOpacity>
            <View style={[styles.dropdownMenuSeparator, { backgroundColor: themedStyles.dropdownMenu.borderColor }]} />
            <TouchableOpacity style={styles.dropdownMenuItem} onPress={handleDelete}>
              <Text style={styles.dropdownMenuItemTextDelete}>
                {t('common.delete')}
              </Text>
              <TrashIcon size={16} color="#EF4444" />
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
};

// ============================================================
// Pagination Component
// ============================================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  if (totalPages <= 1) return null;

  const pages: number[] = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
      {/* Previous: السابق > */}
      <TouchableOpacity
        style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
        onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <Text style={[styles.paginationButtonText, currentPage === 1 && styles.paginationButtonTextDisabled]}>
          {t('common.previous')}
        </Text>
        <ChevronRightIcon size={16} color={currentPage === 1 ? '#A3A3A3' : '#414042'} />
      </TouchableOpacity>

      {/* Page numbers */}
      <View style={[styles.paginationPages, { flexDirection: rtlRow }]}>
        {pages.map((page) => (
          <TouchableOpacity
            key={page}
            style={[styles.paginationPage, currentPage === page && styles.paginationPageActive]}
            onPress={() => onPageChange(page)}
          >
            <Text style={[styles.paginationPageText, currentPage === page && styles.paginationPageTextActive]}>
              {page}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Next: < التالي */}
      <TouchableOpacity
        style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
        onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronLeftIcon size={16} color={currentPage === totalPages ? '#A3A3A3' : '#414042'} />
        <Text style={[styles.paginationButtonText, currentPage === totalPages && styles.paginationButtonTextDisabled]}>
          {t('common.next')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function GoalsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { navigateToHome, isViewOnly, canSeePending, isDecisionMaker } = useRoleNavigation();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { selectedYear } = useYear();

  // Check if user is org_employee (needs delete approval)
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';

  // DTS 2.2 row "إدخال الأهداف الاستراتيجية": only فريق الاستراتيجية
  // (and superuser) creates/edits/deletes strategic goals. Everyone
  // else — including org_employee and section heads — is read-only on
  // this screen. We gate on the actual SSO permission instead of the
  // coarser `isViewOnly` flag, which was wrongly letting org_employee
  // see Add/Edit/Delete affordances when data entry was open.
  const canManageStrategicGoals = (
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.strategic_goals.create')
  );

  // Theme-aware styles
  const themedCardStyles: ThemedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageTitle: { color: colors.gray900 },
    pageSubtitle: { color: colors.textMuted },
    statBox: { backgroundColor: colors.backgroundAlt },
    statLabel: { color: colors.textSecondary },
    progressText: { color: colors.textSecondary },
    detailsText: { color: colors.textSecondary },
    iconColor: colors.iconDefault,
    dropdownMenu: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    dropdownText: { color: colors.textSecondary },
    circleTrack: colors.border,
  };

  // Additional themed colors for header, pagination, modal
  const themeColors = {
    headerCard: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    pageTitle: colors.textPrimary,
    exportButton: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    exportButtonText: colors.textPrimary,
    emptyText: colors.textMuted,
    paginationText: colors.textPrimary,
    paginationTextDisabled: colors.textMuted,
    modalBackground: colors.card,
    modalTitle: colors.textPrimary,
    modalMessage: colors.textMuted,
    cancelButton: colors.backgroundAlt,
    cancelButtonText: colors.textPrimary,
  };

  // Responsive breakpoints (maintain compatibility with existing logic)
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;
  const [refreshing, setRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const pageSize = 6;

  const queryClient = useQueryClient();

  const {
    data: goalsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGoalsQuery({
    page: currentPage,
    pageSize: pageSize,
    department_id: effectiveOrgId,
    // Only executive (decision maker) sees approved goals only; org_executive needs pending goals too (to approve them)
    status: isDecisionMaker ? 'approved' : undefined,
    year: selectedYear,
  });

  const goals = goalsData?.results || [];

  // Auto-refresh when screen regains focus (e.g. after add/edit)
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Update total pages when data changes
  React.useEffect(() => {
    if (goalsData?.count) {
      setTotalPages(Math.ceil(goalsData.count / pageSize));
    }
  }, [goalsData?.count, pageSize]);

  // Reset page when org filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteGoal = (id: number) => {
    setGoalToDelete(id);
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    if (!goalToDelete) return;

    try {
      setIsDeleting(true);
      const response = await goalsApi.delete(goalToDelete);

      // Check if it's a delete request (202) or actual delete (204)
      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t('goals.deleteSuccess'));
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    } catch (err: any) {
      // Check for 202 response in error (some APIs return 202 as success)
      if (err.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        queryClient.invalidateQueries({ queryKey: ['goals'] });
      } else {
        showError(err.response?.data?.detail || t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
      setGoalToDelete(null);
    }
  };

  // Handle Excel export - fetch ALL data, not just current page
  const handleExportExcel = async () => {
    if (goalsData?.count === 0) {
      showError(t('common.noDataToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Fetch ALL goals (not just current page)
      const params: Record<string, any> = { page_size: 9999 };
      if (effectiveOrgId) params.department_id = effectiveOrgId;
      if (isDecisionMaker) params.status = 'approved';

      const allGoalsData = await goalsApi.list(params);
      const allGoals = allGoalsData?.results || [];

      if (allGoals.length === 0) {
        showError(t('common.noDataToExport'));
        return;
      }

      // Strategic KPIs linked directly to each goal (source_type='strategic_goal')
      const goalIds = allGoals.map((g: any) => g.id);
      const kpiResults = await Promise.all(
        goalIds.map((id: number) =>
          kpisApi.list({ strategic_goal: id, source_type: 'strategic_goal', page_size: 9999 })
        )
      );
      const allKpis = kpiResults.flatMap((res: any) => res?.results || []);

      exportGoalsToExcel(allGoals, allKpis, selectedYear);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  // Show skeleton on initial load
  if (isLoading && goals.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={t('common.error')}
        message={error.message || t('common.loadingError')}
        actionLabel={t('common.refresh')}
        onAction={refetch}
      />
    );
  }

  // Calculate grid columns based on screen width
  const numColumns = isSmallScreen ? 1 : isMediumScreen ? 2 : 2;

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding, minHeight: 600 }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('goals.strategicGoals') },
          ]}
        />

        {/* Header Card with title and add button */}
        <View style={[styles.headerCard, isSmallScreen && styles.headerCardSmall, themeColors.headerCard]}>
          <View style={[styles.headerRow, isSmallScreen && styles.headerRowSmall]}>
            {/* Action buttons on the left */}
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {/* Export button */}
              <TouchableOpacity
                style={[styles.exportButton, themeColors.exportButton]}
                onPress={handleExportExcel}
              >
                <Text style={[styles.exportButtonText, { color: themeColors.exportButtonText }]}>{t('common.exportExcel')}</Text>
                <ExportIcon size={13} color={themeColors.exportButtonText} />
              </TouchableOpacity>

              {/* Add Strategic Goal — DTS 2.2 row "إدخال الأهداف
                  الاستراتيجية" gives this only to فريق الاستراتيجية. */}
              {canManageStrategicGoals && (
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={() => navigation.navigate('AddGoal', {})}
                >
                  <Text style={styles.addButtonText}>{t('goals.addNewGoal')}</Text>
                  <PlusIcon size={13} color="#FFFFFF" />
                </TouchableOpacity>
              )}
            </View>

            {/* Title on the right */}
            <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{t('goals.strategicGoals')}</Text>
          </View>

          {/* Filter Row - org filter (only for multi-org users) */}
          {canViewAllOrgs && (
            <View style={styles.orgFilterRow}>
              <OrganizationFilterDropdown
                selectedOrgId={selectedOrgId}
                onChange={setSelectedOrgId}
              />
            </View>
          )}
        </View>

        {/* Goals Grid */}
        {goals.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.emptyText }]}>{t('goals.noStrategicGoals')}</Text>
          </View>
        ) : (
          <View style={[styles.goalsGrid, { flexDirection: isSmallScreen ? 'column' : 'row-reverse', flexWrap: 'wrap' }]}>
            {goals.map((goal) => (
              <View
                key={goal.id}
                style={[
                  styles.goalCardWrapper,
                  {
                    width: isSmallScreen ? '100%' : numColumns === 2 ? '48.5%' : '31.5%',
                    marginBottom: 20,
                  },
                ]}
              >
                <GoalCard
                  goal={goal}
                  onPress={() => navigation.navigate('GoalDetail', { id: goal.id })}
                  onEdit={(id) => navigation.navigate('AddGoal', { goalId: id })}
                  onDelete={(id) => handleDeleteGoal(id)}
                  isOrgEmployee={isOrgEmployee}
                  isViewOnly={!canManageStrategicGoals}
                  themedStyles={themedCardStyles}
                />
              </View>
            ))}
          </View>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setGoalToDelete(null); }}
        onConfirm={confirmDelete}
        entityName={goals.find(g => g.id === goalToDelete)?.title || ''}
        fetchImpact={async () => {
          const response = await goalsApi.deletionImpact(goalToDelete!);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles (matching Figma exactly)
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    // Padding set dynamically via inline style for responsiveness
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
    height: SKELETON_DIMENSIONS.BREADCRUMB_HEIGHT,
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 12,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Header Card (matches Figma node 2432:365759)
  headerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 24,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    minHeight: SKELETON_DIMENSIONS.PAGE_TITLE_HEIGHT,
  },
  headerCardSmall: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerRowSmall: {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: 12,
  },
  orgFilterRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginTop: 12,
    zIndex: 100,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 26,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#A9211F',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    minHeight: 44,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#F8FAFC',
    lineHeight: 20,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    minHeight: 44,
  },
  exportButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    lineHeight: 20,
  },
  // Goals Grid
  goalsGrid: {
    gap: 20,
    justifyContent: 'flex-start',
  },
  goalCardWrapper: {
    // Dynamic width set inline
    height: SKELETON_DIMENSIONS.GOAL_CARD_HEIGHT,
  },
  // Goal Card styles (matches Figma node 2432:365762)
  goalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    position: 'relative',
    zIndex: 1,
    height: SKELETON_DIMENSIONS.GOAL_CARD_HEIGHT,
    overflow: 'hidden', // Prevent content overflow
  },
  goalCardMenuOpen: {
    zIndex: 1000,
  },
  goalCardTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  goalCardContent: {
    flex: 1,
    alignItems: 'flex-end',
    gap: 12,
  },
  goalCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  goalCardDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
  },
  progressPercentage: {
    fontSize: 16,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
  },
  // Pending badge
  pendingBadge: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
  },
  pendingBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  // Stats row - individual boxes (matches Figma exactly)
  goalCardStatsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
  },
  goalCardStatBox: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: 'flex-end',
    gap: 4,
  },
  goalCardStatLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
  },
  goalCardStatValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'center',
  },
  // Bottom row
  goalCardBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  menuWrapper: {
    position: 'relative',
  },
  goalCardMenu: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Dropdown menu styles
  dropdownMenu: {
    position: 'absolute',
    bottom: 40,
    right: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.25)',
    elevation: 20,
    minWidth: 160,
    zIndex: 1002,
    paddingVertical: 8,
  },
  dropdownMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  dropdownMenuItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownMenuItemTextDelete: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
  },
  dropdownMenuSeparator: {
    height: 1,
    backgroundColor: '#E2E8F0',
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1001,
    backgroundColor: 'transparent',
  },
  // Details link
  goalCardDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 10,
    minHeight: 44,
    borderRadius: 4,
  },
  goalCardDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Pagination
  paginationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 24,
    paddingVertical: 16,
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    minHeight: 44,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  paginationButtonTextDisabled: {
    color: '#A3A3A3',
  },
  paginationPages: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  paginationPage: {
    width: 44,
    height: 44,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  paginationPageActive: {
    backgroundColor: '#A9211F',
  },
  paginationPageText: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    color: '#414042',
  },
  paginationPageTextActive: {
    color: '#FFFFFF',
  },
  // Empty state
  emptyContainer: {
    padding: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: 'center',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  modalDeleteButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  modalDeleteButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
});
